import gql from 'graphql-tag';

import { listResourceQueryDefinitions, deleteResourceMutationDefinitions } from '@/providers/graphql/resourceQueryDefinitions';

export const CONTRACTS_LIST_QUERY = gql`
        query ContractsList($first: Int, $after: String) {
                contracts(first: $first, after: $after) {
                        nodes {
                                contractId
                                contractNumber
                                beginDate
                                endDate
                                amount
                                simultaneousVehicleLimit
                                reservedParkingSlots
                                isActive
                                isAutoRenewed
                                preferredLanguage
                                includeAllCustomerVehicles
                                customer {
                                        customerId
                                        firmName
                                        firstName
                                        lastName
                                        isCompany
                                        customerCategory {
                                                description
                                                simultaneousVehicleLimit
                                        }
                                }
                                vehicles {
                                        contractId
                                        vehicleId
                                        vehicle {
                                                vehicleId
                                                plateNumber
                                                make
                                                model
                                                color
                                        }
                                }
                                vipVehicles
                                cards {
                                        contractCardId
                                        facilityId
                                        cardType
                                        cardKey
                                        cardStatusId
                                        depositTransactionId
                                        returnTransactionId
                                }
                                parkingSessions {
                                        parkingSessionId
                                        entryDateTime
                                        exitDateTime
                                        status
                                }
                        }
                        pageInfo {
                                hasNextPage
                                endCursor
                        }
                }
        }
`;

export const CONTRACT_LIST_ITEM_QUERY = gql`
        query ContractListItem($contractId: Ulid!) {
                contract(contractId: $contractId) {
                        contractId
                        contractNumber
                        beginDate
                        endDate
                        amount
                        simultaneousVehicleLimit
                        reservedParkingSlots
                        isActive
                        isAutoRenewed
                        preferredLanguage
                        includeAllCustomerVehicles
                        customer {
                                customerId
                                firmName
                                firstName
                                lastName
                                isCompany
                                customerCategory {
                                        description
                                        simultaneousVehicleLimit
                                }
                                vehicles {
                                        vehicleId
                                        plateNumber
                                        vehicleType {
                                                name
                                        }
                                        make
                                        model
                                        color
                                }
                        }
                        vehicles {
                                contractId
                                vehicleId
                                vehicle {
                                        vehicleId
                                        plateNumber
                                        make
                                        model
                                        color
                                }
                        }
                        vipVehicles
                        cards {
                                contractCardId
                                facilityId
                                cardType
                                cardKey
                                cardStatusId
                                depositTransactionId
                                returnTransactionId
                        }
                        parkingSessions {
                                parkingSessionId
                                entryDateTime
                                exitDateTime
                                status
                        }
                }
        }
`;

export const CONTRACT_DETAIL_QUERY = gql`
        query ContractDetail($contractId: Ulid!) {
                contract(contractId: $contractId) {
                        contractId
                        contractNumber
                        beginDate
                        endDate
                        amount
                        isAutoRenewed
                        simultaneousVehicleLimit
                        reservedParkingSlots
                        isActive
                        preferredLanguage
                        includeAllCustomerVehicles
                        customer {
                                customerId
                                firmName
                                firstName
                                lastName
                                isCompany
                                customerCategory {
                                        description
                                        simultaneousVehicleLimit
                                }
                                vehicles {
                                        vehicleId
                                        plateNumber
                                        vehicleType {
                                                name
                                        }
                                        make
                                        model
                                        color
                                }
                        }
                        vehicles {
                                contractId
                                vehicleId
                                vehicle {
                                        plateNumber
                                        vehicleType {
                                                name
                                        }
                                        make
                                        model
                                        color
                                }
                        }
                        vipVehicles
                        cards {
                                contractCardId
                                facilityId
                                cardType
                                cardKey
                                cardStatusId
                                depositTransactionId
                                returnTransactionId
                                depositTransaction {
                                        grossAmount
                                }
                        }
                        parkingSessions {
                                parkingSessionId
                                entryDateTime
                                exitDateTime
                                status
                        }
                }
        }
`;

export const CONTRACTS_PANEL_BY_CUSTOMER_QUERY = gql`
	query ContractsPanelByCustomer(
		$customerId: Ulid!
		$first: Int
		$after: String
	) {
		contracts(
			first: $first
			after: $after
			order: [{ beginDate: DESC }]
			where: { customerId: { eq: $customerId } }
		) {
			nodes {
				contractId
				contractNumber
				customerId
				beginDate
				endDate
				amount
				simultaneousVehicleLimit
				isActive
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

export const CONTRACTS_PANEL_BY_VEHICLE_QUERY = gql`
	query ContractsPanelByVehicle($vehicleId: Ulid!) {
		vehicle(vehicleId: $vehicleId) {
			vehicleId
			contractVehicles {
				contractId
				vehicleId
				contract {
					contractId
					contractNumber
					beginDate
					endDate
					amount
					simultaneousVehicleLimit
					isActive
				}
			}
		}
	}
`;



export const CREATE_CONTRACT_MUTATION = gql`
        mutation CreateContract($input: CreateContractInput!) {
                createContract(input: $input) {
                        success
                        errorCode
                        errorDescription
                        isErrored
                        failed
                }
        }
`;

export const UPDATE_CONTRACT_MUTATION = gql`
        mutation UpdateContract($input: UpdateContractInput!) {
                updateContract(input: $input) {
                        success
                        errorCode
                        errorDescription
                        isErrored
                        failed
                }
        }
`;

export const DELETE_CONTRACT_MUTATION = gql`
        mutation DeleteContract($input: DeleteContractInput!) {
                deleteContract(input: $input) {
                        success
                        errorCode
                        errorDescription
                        isErrored
                        failed
                }
        }
`;

export const CONTRACTS_AUTOCOMPLETE_QUERY = gql`
	query ContractsAutocomplete(
		$where: ContractViewModelFilterInput
		$first: Int!
		$after: String
		$order: [ContractViewModelSortInput!]
	) {
		contracts(first: $first, after: $after, where: $where, order: $order) {
			edges {
				node {
					contractId
					contractNumber
					isActive
					beginDate
					endDate
					customer {
						customerId
						firmName
						firstName
						lastName
						isCompany
					}
				}
				cursor
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

// Lightweight query to fetch effective vehicles for a single contract (used after selection)
export const CONTRACT_EFFECTIVE_VEHICLES_QUERY = gql`
	query ContractEffectiveVehicles($contractId: Ulid!) {
		contract(contractId: $contractId) {
			contractId
			includeAllCustomerVehicles
			customerId
			effectiveVehicles {
				vehicleId
				plateNumber
				make
				model
				color
				vehicleType {
					name
				}
			}
		}
	}
`;

// Lookup queries for form options
export const CUSTOMERS_QUERY = gql`
        query Customers {
                customers(first: 50) {
                        nodes {
                                customerId
                                firmName
                                email
                                customerCategory {
                                        description
                                        simultaneousVehicleLimit
                                }
                        }
                }
        }
`;

export const VEHICLES_QUERY = gql`
        query Vehicles {
                vehicles(first: 50) {
                        nodes {
                                vehicleId
                                plateNumber
                                make
                                model
                                color
                        }
                }
        }
`;

listResourceQueryDefinitions['contracts'] = {
        document: CONTRACTS_LIST_QUERY,
        dataPath: 'contracts.nodes'
};

deleteResourceMutationDefinitions['contracts'] = {
	document: DELETE_CONTRACT_MUTATION,
	operation: 'deleteContract',
	getVariables: variables => ({ input: variables })
};
